import React, { SyntheticEvent, useEffect } from 'react';

import { Helmet } from 'react-helmet';
import { useLocation, useParams } from 'react-router-dom';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Box, Button, Container, Stack, Tab, Tabs, Typography } from '@mui/material';

import PageHeader from '~/components/Common/Elements/PageHeader';

import useAppNavigation from '~/hooks/useAppNavigation';

import TripPlannerChatTracePage from './TripPlannerChatTracePage';
import { tripPlannerChatTabs } from './tripPlannerChatTabs';

export default function TripPlannerChatPage() {
  const { push, replace } = useAppNavigation();
  const location = useLocation();
  const { chatId } = useParams<{ chatId?: string }>();

  const currentTab = tripPlannerChatTabs.find((tab) => tab.path === location.pathname) ?? tripPlannerChatTabs[0];
  const chatListParams = new URLSearchParams(location.search);
  chatListParams.delete('turnId');
  chatListParams.delete('messageId');
  const tripsTabPath = tripPlannerChatTabs[0]?.path ?? '';

  const handleTabChange = (_event: SyntheticEvent, newValue: string) => {
    push({
      pathname: newValue,
      search: newValue === tripsTabPath ? location.search : '',
    });
  };
  const CurrentTabComponent = currentTab.component;
  const pageTitle = chatId ? 'Trace' : currentTab.title;

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (!chatId || !searchParams.has('chatId')) {
      return;
    }
    searchParams.delete('chatId');
    replace({ pathname: location.pathname, search: searchParams.toString(), hash: location.hash }, location.state);
  }, [chatId, location.pathname, location.search, location.hash, location.state, replace]);

  return (
    <Container>
      <Helmet>
        <title>TripPlanner - Chat - {pageTitle}</title>
      </Helmet>

      {chatId ? (
        <TripPlannerChatTracePage
          renderHeader={(actions) => (
            <Stack
              direction="row"
              alignItems="center"
              flexWrap="wrap"
              gap={1}
              sx={{ py: 1.5, borderBottom: 1, borderColor: 'divider' }}
            >
              <Button
                startIcon={<ArrowBackIcon />}
                size="small"
                sx={{ textTransform: 'none' }}
                onClick={() => push({ pathname: tripsTabPath, search: chatListParams.toString() })}
              >
                Chats
              </Button>
              <Typography variant="subtitle2" sx={{ borderLeft: 1, borderColor: 'divider', pl: 2, mr: 1 }}>
                Chat {chatId}
              </Typography>
              <Box sx={{ ml: 'auto' }}>{actions}</Box>
            </Stack>
          )}
        />
      ) : (
        <>
          <PageHeader title={`TripPlanner / ${currentTab.title}`} />
          <Box
            sx={{
              alignItems: 'center',
              bgcolor: 'background.paper',
              border: 1,
              borderColor: 'divider',
              borderRadius: 2,
              display: 'inline-flex',
              maxWidth: '100%',
              px: 0.5,
              py: 0.25,
            }}
          >
            <Tabs
              value={currentTab.path}
              onChange={handleTabChange}
              aria-label="TripPlanner pages"
              TabIndicatorProps={{ sx: { display: 'none' } }}
              sx={{
                minHeight: 32,
                '& .MuiTabs-flexContainer': { gap: 0.5 },
                '& .MuiTab-root': {
                  borderRadius: 1.5,
                  color: 'text.primary',
                  fontWeight: 600,
                  minHeight: 32,
                  px: 2,
                  py: 0.5,
                  textTransform: 'none',
                  '&:hover': { bgcolor: 'action.hover' },
                },
                '&& .MuiTab-root.Mui-selected': {
                  bgcolor: 'action.selected',
                  color: 'primary.main',
                },
              }}
            >
              {tripPlannerChatTabs.map((tab) => (
                <Tab key={tab.path} disableRipple label={tab.title} value={tab.path} />
              ))}
            </Tabs>
          </Box>
          <Box sx={{ mt: 3 }}>
            <CurrentTabComponent />
          </Box>
        </>
      )}
    </Container>
  );
}
